import { useCallback, useRef } from 'react';

import type { Rainv1Value } from '@/generated/hooks/rain/models';
import type { Commonv1Value } from '@/generated/hooks/grimoire/models';

import type { IRainToolCallState } from '../hooks/types';
import { commonValuesToRainInputsArgument } from '../utils/value';
import { EQuillVisibleTool } from './quill';
import {
  extractPreviousRunId,
  extractTestAutomationRunId,
} from './quill-run-result';

interface IUseQuillTestRunParams {
  /** The thread's `sendToolCall`; resolves with the server-minted call id. */
  sendToolCall: (
    toolName: string,
    args: Record<string, Rainv1Value>,
  ) => Promise<string | undefined>;
  /** Run id currently on the step view, if any. */
  focusedRunId?: string;
  /**
   * Focus the step view on a run. Absent on a surface with no run view, which
   * makes both auto-focus rules inert.
   */
  onFocusTestRun?: (
    run:
      | { toolCallId: string; runId: string; startedAtMs?: number }
      | undefined,
  ) => void;
}

export interface IUseQuillTestRunReturn {
  /** Runs the automation as a deterministic `test_automation` call. */
  sendTestRun: (inputs: Record<string, Commonv1Value>) => Promise<void>;
  /** Pass to `useRainThreadMessages` as `onVisibleToolResult`. */
  handleToolResult: (state: IRainToolCallState) => void;
}

/**
 * quill2's Test button and the two rules that move the step view onto a run
 * without the user clicking its chat card:
 *
 *   1. A run the Test button started focuses itself once its id is known.
 *   2. An organic `replay_run` — quill fixing an awaiting-guidance run and
 *      re-running it — focuses its new run, but only once `previous_run`
 *      confirms it continues whatever is already focused. `previous_run` is set
 *      by the ReplayRun RPC, so this is the backend's own link rather than
 *      something inferred from the call's arguments. Anything else leaves focus
 *      alone, and the user can still open the run from its card.
 */
export function useQuillTestRun({
  sendToolCall,
  focusedRunId,
  onFocusTestRun,
}: IUseQuillTestRunParams): IUseQuillTestRunReturn {
  // Marks the call the Test button started, so rule 1 can recognise its result.
  const pendingAutoFocusToolCallIdRef = useRef<string | undefined>(undefined);
  const focusedRunIdRef = useRef(focusedRunId);
  focusedRunIdRef.current = focusedRunId;
  const onFocusTestRunRef = useRef(onFocusTestRun);
  onFocusTestRunRef.current = onFocusTestRun;

  /**
   * `test_automation`'s `inputs` parameter is a single Rain value wrapping the
   * whole input record as Quill's TaggedValue dictionary. The values start life
   * as Grimoire `Commonv1Value`s (the input sheet's shape), so they need
   * `commonValuesToRainInputsArgument`; sending them as top-level arguments, or
   * as bare `Commonv1Value`s cast to `Rainv1Value`, both fail server-side
   * validation.
   *
   * `triggered_by: "user"` separates this UI-initiated call from the LLM
   * calling the same tool itself, so the agent needn't infer intent.
   */
  const sendTestRun = useCallback(
    async (inputs: Record<string, Commonv1Value>): Promise<void> => {
      const toolCallId = await sendToolCall(EQuillVisibleTool.TestAutomation, {
        inputs: commonValuesToRainInputsArgument(inputs),
        triggered_by: { string_value: 'user' },
      });
      if (onFocusTestRunRef.current && toolCallId) {
        pendingAutoFocusToolCallIdRef.current = toolCallId;
      }
    },
    [sendToolCall],
  );

  const handleToolResult = useCallback((state: IRainToolCallState): void => {
    const onFocus = onFocusTestRunRef.current;
    if (!onFocus) return;

    if (state.id === pendingAutoFocusToolCallIdRef.current) {
      pendingAutoFocusToolCallIdRef.current = undefined;
      const runId = extractTestAutomationRunId(state.result);
      if (runId) {
        onFocus({
          toolCallId: state.id,
          runId,
          startedAtMs: state.startedAtMs,
        });
      }
      return;
    }

    if (state.name !== EQuillVisibleTool.ReplayRun) return;
    const previousRunId = extractPreviousRunId(state.result);
    if (!previousRunId || previousRunId !== focusedRunIdRef.current) return;
    const runId = extractTestAutomationRunId(state.result);
    if (runId) {
      onFocus({ toolCallId: state.id, runId, startedAtMs: state.startedAtMs });
    }
  }, []);

  return { sendTestRun, handleToolResult };
}
